import { React, useState, useRef, useEffect } from 'react';
import Multiselect from 'multiselect-react-dropdown';
import {
  Row,
  Col,
  CardGroup,
} from 'react-bootstrap';
import { Search } from '../../services/Contexture/Search';
import DocCardMitreAttack from './DocCardMitreAttack';
import Loading from '../DocList/Loading';
import AlertMessage from '../AlertMessage';
import { IoSearchCircleSharp } from "react-icons/io5";
import { Metadata } from '../../services/Contexture/Metadata';
import ReactPaginate from 'react-paginate';
import DocListSkeleton from '../DocList/DocListSkeleton';

const fallback = [
  { name: "Asset", id: "x-mitre-asset" },
  { name: "Campaign", id: "campaign" },
  { name: "Data Component", id: "x-mitre-data-component" },
  { name: "Data Source", id: "x-mitre-data-source" },
  { name: "Group", id: "intrusion-set" },
  { name: "Identity", id: "identity" },
  { name: "Mitigation", id: "course-of-action" },
  { name: "Software (Malware)", id: "malware" },
  { name: "Software (Tool)", id: "tool" },
  { name: "Tactic", id: "x-mitre-tactic" },
  { name: "Technique", id: "attack-pattern" }
]

const SearchMitreAttack = ({
  profile,
  profileSecondary,
  showAddWatchList,
  showRemove,
}) => {
  const PAGE_SIZE = 10;
  const [docTypes, setDocTypes] = useState([]);
  const excludedObjectTypes = new Set(['relationship', 'marking-definition', 'x-mitre-matrix']);
  const [pageCount, setPageCount] = useState(0);

  useEffect(() => {
    const fetchObjectTypeMetadata = async () => {
      try {
        const response = await Metadata.fetchFields(profile.repositoryId, { field_name: 'objectType' });
        const data = response?.fields?.[0]?.validValues;
        const options = (data || [])
          .filter(({ value }) => !excludedObjectTypes.has(value))
          .map(({ label, value }) => ({ name: label, id: value }));
        setDocTypes(options);
        setSearchOptions(options);
      } catch (error) {
        console.error('Failed to fetch objectType metadata:', error);
        // Fallback to minimal set if metadata fetch fails
        setDocTypes(fallback);
        setSearchOptions(fallback);
      }
    };
    if (profile?.repositoryId) {
      fetchObjectTypeMetadata();
    }
  }, [profile?.repositoryId]);

  const sortSettings = {
    title: {
      label: 'Title',
      direction: 'DESC',
    },
    author: { label: 'Author', direction: 'DESC', active: false },
    site: { label: 'Site', direction: 'DESC', active: false },
    publishDate: { label: 'Publish Date', direction: 'DESC', active: true },
  };
  const [sortLoader, setSortLoader] = useState(false);
  const [sortBy, setSortBy] = useState(sortSettings);
  const [isFetching, setIsFetching] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [docs, setDocs] = useState({
    items: [],
    nextToken: 0,
  });
  const [searchOptions, setSearchOptions] = useState([]);
  const [selectedSearchValue, setSelectedSearchValue] = useState([]);
  const [sortValue, setSortValue] = useState(['publishDate-']);
  const [searchtextValue, setSearchtextValue] = useState('');
  const selectSearchInputRef = useRef();
  const [activeFilters, setActiveFilters] = useState({ searchText: '', tags: [] });
  const [currentPage, setCurrentPage] = useState(0);
  const [isSearchTriggered, setIsSearchTriggered] = useState(false);

  useEffect(() => {
    if (pageCount && currentPage > pageCount - 1) {
      setCurrentPage(pageCount - 1);
    }
  }, [currentPage, pageCount]);

  useEffect(() => {
    if (!isSearchTriggered) return;
    const criteria = {
      type: 'and',
      children: [
        ...(activeFilters.tags && activeFilters.tags.length > 0
          ? [
            {
              type: 'terms',
              fieldName: 'objectType',
              values: activeFilters.tags.map((t) => t.id),
            },
          ]
          : [
            {
              type: 'terms',
              fieldName: 'objectType',
              values: docTypes.map((t) => t.id),
            }
          ]),
        ...(activeFilters.searchText
          ? [
            {
              type: 'multiMatch',
              fieldNames: ['title', 'text'],
              value: activeFilters.searchText,
            },
          ]
          : []),
      ],
    };

    let isMounted = true;
    const fetchPage = async () => {
      if (!docTypes.length) {
        if (isMounted) {
          setIsFetching(false);
          setSortLoader(false);
        }
        return;
      }
      setIsFetching(true);
      setSortLoader(true);
      try {
        const res = await Search.search({ criteria }, {
          profileId: profile.profileId,
          repositoryId: profile.repositoryId,
          first_result: currentPage * PAGE_SIZE + 1,
          max_results: PAGE_SIZE,
        });
        const newItems = res.response?.documents || res.documents || [];
        if (!isMounted) return;
        setDocs({
          items: newItems,
          nextToken: res.response?.lastResult || res.lastResult || 0,
        });
        const total = res.response?.totalHits || res.totalHits || 0;
        setTotalRecords(total);
        setPageCount(Math.ceil(total / PAGE_SIZE) || 0);
      } catch (error) {
        if (isMounted) {
          console.error('Error loading Mitre items:', error);
          setDocs({ items: [], nextToken: 0 });
          setTotalRecords(0);
          setPageCount(0);
        }
      } finally {
        if (isMounted) {
          setIsFetching(false);
          setSortLoader(false);
        }
      }
    };

    fetchPage();
    return () => {
      isMounted = false;
    };
  }, [activeFilters, currentPage, docTypes, isSearchTriggered, PAGE_SIZE, profile.profileId, profile.repositoryId]);

  const onSelect = (selectedList, selectedItem) => {
    setSelectedSearchValue([...selectedList]);
  };

  // Handle scroll to top when manually changing pages
  const handlePageChange = ({ selected }) => {
    setCurrentPage(selected);
    window.scrollTo(0, 0);
  };  

  const onRemove = (selectedList, removedItem) => {
    setSelectedSearchValue([...selectedList]);
  };

  const handlesearchInput = (e) => {
    setSearchtextValue(e.target.value);
  };

  const handleResetSearch = () => {
    setSearchtextValue('');
    setSelectedSearchValue([]);
    setActiveFilters({ searchText: '', tags: [] });
    setDocs({ items: [], nextToken: 0 });
    setTotalRecords(0);
    setPageCount(0);
    setIsSearchTriggered(false);
    if (selectSearchInputRef.current) {
      selectSearchInputRef.current.resetSelectedValues();
    }
  };

  const onSort = (field) => {
    setIsFetching(true);
    setSortLoader(true);
    if (field.active && field.direction === 'DESC') {
      field.direction = 'ASC';
    } else if (field.active && field.direction === 'ASC') {
      field.direction = 'DESC';
      field.active = false;
    } else if (!field.active) {
      field.active = true;
    }
    let sort = { ...sortBy };
    const sort_by = Object.keys(sortBy)
      .filter((field, index) => sortBy[field].active)
      .map((f) => `${f}${sortBy[f].direction === 'ASC' ? '+' : '-'}`);
    setSortValue(sort_by);
    setSortBy(sort);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const types = selectedSearchValue.map((v) => v.id);
    if (types.length == 0 && searchtextValue == '') return;
    setIsSearchTriggered(true);
    setIsFetching(true);
    setSortLoader(true);
    setCurrentPage(0);
    setActiveFilters({
      searchText: searchtextValue,
      tags: selectedSearchValue,
    });
  };

  return (
    <>
      <Row>
        <Col lg={12}>
          <form
            onSubmit={handleSearch}
            className='ctx-search-form'
          //style={{ display: 'flex', padding: '15px 20px' }}
          >
            <input
              className='ctx-search'
              placeholder='Keywords'
              name='search'
              type='text'
              style={{ marginRight: '20px', padding: '0 10px' }}
              value={searchtextValue}
              onChange={handlesearchInput}
            />
            <Multiselect
              ref={selectSearchInputRef}
              options={docTypes?.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))}
              closeOnSelect={true}
              selectedValues={selectedSearchValue}
              onSelect={onSelect}
              onRemove={onRemove}
              displayValue='name'
              placeholder='Document Types'
            />
            <button type='submit' className='ctx-search-btn'>
              Search
            </button>
            <button onClick={handleResetSearch} className='ctx-search-btn'>
              Reset
            </button>
          </form>
        </Col>
      </Row>
      <Row>
        {/* <Col lg={12}>
          <ListGroup horizontal>
            {Object.keys(sortBy).map((field, index) => (
              <ListGroupItem
                key={index}
                className='flex-fill'
                style={{ textAlign: 'center' }}
              >
                <Button
                  variant='outline-secondary'
                  onClick={() => onSort(sortBy[field])}
                  active={sortBy[field].active && true}
                >
                  {sortBy[field].label}
                  {sortBy[field].active && (
                    <FontAwesomeIcon
                      icon={[
                        'fal',
                        `${
                          sortBy[field].direction === 'DESC'
                            ? 'caret-down'
                            : 'caret-up'
                        }`,
                      ]}
                      style={{ fontSize: '1.5rem', marginLeft: '10px' }}
                    ></FontAwesomeIcon>
                  )}
                </Button>
              </ListGroupItem>
            ))}
          </ListGroup>
        </Col>*/}
        <CardGroup
          className={`ctx-card-list ${sortLoader ? 'sort-loading-overlay' : ''
            }`}
        >
          {!isSearchTriggered && docs.items.length === 0 && !isFetching && (
            <div className="d-flex flex-column align-items-center justify-content-center w-100">
              <IoSearchCircleSharp size={60} style={{ opacity: 0.6 }} />
              <h4 className="mt-3 fw-bold">Search to explore MITRE ATT&CK data</h4>
              <p className="text-muted">Start by entering a keyword or selecting document types above</p>
            </div>
          )}
          {!isFetching && docs.items && docs.items.length ? (
            <>
              {docs.items.map((doc) => (
                <DocCardMitreAttack
                  doc={doc}
                  showAddWatchList={showAddWatchList}
                  list='mitre_search'
                  showRemove={showRemove}
                  profilePrimary={profile}
                  profileSecondary={profileSecondary}
                  key={doc.id}
                  allTags={searchOptions}
                  selectedTags={selectedSearchValue.map((t) => t.name)}
                />
              ))}
            </>
          ) : null}
          {isFetching ? <DocListSkeleton />
            : null}

          {pageCount > 1 ? (
            <ReactPaginate
              breakLabel='...'
              nextLabel='Next'
              onPageChange={handlePageChange}
              pageRangeDisplayed={3}
              marginPagesDisplayed={1}
              pageCount={pageCount}
              previousLabel='Previous'
              forcePage={currentPage}
              containerClassName='pagination justify-content-center ctx-pagination'
              pageClassName='page-item'
              pageLinkClassName='page-link'
              previousClassName='page-item'
              previousLinkClassName='page-link'
              nextClassName='page-item'
              nextLinkClassName='page-link'
              activeClassName='active'
              disabledClassName='disabled'
            />
          ) : null}
          
          {docs.items.length === 0 && isFetching == false && isSearchTriggered ? (
            <AlertMessage variant='danger' message='No Record Found!' />
          ) : null}
        </CardGroup>
      </Row>
    </>
  );
};
export default SearchMitreAttack;
